import { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import { Link, useParams } from 'react-router-dom';

import { getParameterizedLink } from '@/utils/getLinkWithSubscriptionId.utils';
import { mapEnumToFormattedUsageDetailsStatus } from '../helpers/getUsageDetailsHelpers';
import { VarBillingUsageDetailOutput } from '@/graphql/contracts/_generated_/contracts';
import { FormattingUtils } from '@/utils/Formatting.utils';
import { STATUS_MESSAGE_CHAR_LENGTH_TO_SHOW } from '@/config/common.config';
import { NumberUtils } from '@/utils/numbers.utils';
import RowCustomCheckbox from './components/RowCustomCheckbox';
import { contractsBCPath } from '@/utils/path.utlis';

export function useColumns(): ColDef<VarBillingUsageDetailOutput>[] {
  const { fileId }: { fileId: string } = useParams();

  return [
    {
      colId: 'grid-checkbox',
      headerName: '',
      minWidth: 50,
      maxWidth: 50,
      width: 50,
      flex: 0,
      resizable: false,
      sortable: false,
      cellClass: 'p-0',
      headerClass: 'p-0',
      lockPosition: true,
      pinned: 'left',
      lockPinned: true,
      suppressSizeToFit: true,
      cellRenderer: RowCustomCheckbox,
    },
    {
      headerName: 'Customer Code',
      field: 'customerCode',
      resizable: false,
      initialWidth: 170,
      minWidth: 170,
      maxWidth: 200,
    },
    {
      headerName: 'External Contract ID',
      field: 'externalContractID',
      resizable: false,
      initialWidth: 200,
      minWidth: 200,
      maxWidth: 250,
    },
    {
      headerName: 'Product Code',
      field: 'productCode',
      resizable: false,
      initialWidth: 200,
      minWidth: 200,
      maxWidth: 250,
    },
    {
      headerName: 'Quantity',
      field: 'quantity',
      resizable: false,
      initialWidth: 100,
      minWidth: 100,
      maxWidth: 130,
      cellStyle: () => ({ textAlign: 'right' }),
    },
    {
      headerName: 'Usage Net Value',
      field: 'net',
      resizable: false,
      valueFormatter: ({ data }: ValueFormatterParams<VarBillingUsageDetailOutput>) => {
        return data?.net ? NumberUtils.prettifyWithRound(data.net) : null;
      },
      initialWidth: 150,
      minWidth: 150,
      maxWidth: 180,
      cellStyle: () => ({ textAlign: 'right' }),
    },
    {
      headerName: 'Unit Price',
      field: 'price',
      resizable: false,
      valueFormatter: ({ data }: ValueFormatterParams<VarBillingUsageDetailOutput>) => {
        return data?.price ? NumberUtils.prettifyWithRound(data.price) : null;
      },
      initialWidth: 150,
      minWidth: 150,
      maxWidth: 180,
      cellStyle: () => ({ textAlign: 'right' }),
    },
    {
      headerName: 'Comment',
      field: 'comment',
      resizable: false,
      initialWidth: 150,
      minWidth: 150,
      maxWidth: 180,
    },
    {
      headerName: 'Contract Number',
      field: 'contractNumber',
      resizable: false,
      cellRenderer: ({ data }: ICellRendererParams<VarBillingUsageDetailOutput>) => {
        let link = `/variable-billing/${fileId}/${data?.contractID ?? ''}`;
        link = `/variable-billing/${fileId}/${contractsBCPath}/${data?.contractID ?? ''}`;
        return (
          <Link to={getParameterizedLink(link)}>
            {data?.contractNumber}
          </Link>
        );
      },
      initialWidth: 200,
      minWidth: 200,
      maxWidth: 220,
    },
    {
      headerName: 'Status',
      field: 'status',
      resizable: false,
      cellRenderer: ({ data }: ICellRendererParams<VarBillingUsageDetailOutput>) => {
        return data?.status ? mapEnumToFormattedUsageDetailsStatus(data.status) : undefined;
      },
      initialWidth: 200,
      minWidth: 200,
      maxWidth: 200,
    },
    {
      headerName: 'Sales Order ID',
      field: 'salesOrderID',
      resizable: false,
      initialWidth: 180,
      minWidth: 180,
      maxWidth: 200,
      cellRenderer: ({ value }: ICellRendererParams<VarBillingUsageDetailOutput>) => {
        if (!value) return null;

        return (
          <Link to={getParameterizedLink(`/variable-billing/${fileId}/billing-new?salesOrderId=${value}`)}>
            {value}
          </Link>
        );
      },
    },
    {
      headerName: 'Sales Order by BC ID',
      field: 'salesOrderByBillingCycleID',
      resizable: false,
      initialWidth: 180,
      minWidth: 180,
      maxWidth: 200,
      cellRenderer: ({ value }: ICellRendererParams<VarBillingUsageDetailOutput>) => {
        if (!value) return null;

        return (
          <Link to={getParameterizedLink(`/variable-billing/${fileId}/billing-bc?salesOrderId=${value}`)}>
            {value}
          </Link>
        );
      },
    },
    {
      headerName: 'Dimensions Invoice ID',
      field: 'dimensionsInvoiceID',
      resizable: false,
      initialWidth: 200,
      minWidth: 200,
      maxWidth: 230,
    },
  ];
}

export const statusMessageDynamicColumn = {
  headerName: 'Status Message',
  field: 'statusMessage',
  colId: 'statusMessage',
  initialWidth: 360,
  minWidth: 360,
  maxWidth: 380,
  resizable: false,
  cellRenderer: (params: ICellRendererParams<VarBillingUsageDetailOutput>) => {
    const title = FormattingUtils.getTitleIfValueIsTrimmed(params.value, STATUS_MESSAGE_CHAR_LENGTH_TO_SHOW);
    return <div title={title}>{FormattingUtils.getValueTrimmed(params.value, STATUS_MESSAGE_CHAR_LENGTH_TO_SHOW)}</div>;
  },
};

export const predictedNetValueDynamicColumn = {
  headerName: 'Predicted Net Value',
  field: 'calculatedNet',
  colId: 'calculatedNet',
  valueFormatter: ({ data }: ValueFormatterParams<VarBillingUsageDetailOutput>) => {
    return data?.calculatedNet ? NumberUtils.prettifyWithRound(data.calculatedNet) : null;
  },
  initialWidth: 180,
  minWidth: 170,
  maxWidth: 200,
  cellStyle: () => ({ textAlign: 'right' }),
};

export const pricingModelDynamicColumn = {
  headerName: 'Pricing Model',
  field: 'pricingModel',
  colId: 'pricingModel',
  initialWidth: 180,
  minWidth: 170,
  maxWidth: 200,
};

export const ignoreCommentDynamicColumn = {
  headerName: 'Ignore Comment',
  field: 'ignoreComment',
  colId: 'ignoreComment',
  initialWidth: 300,
  minWidth: 200,
  maxWidth: 400,
  resizable: false,
  cellRenderer: (params: ICellRendererParams<VarBillingUsageDetailOutput>) => {
    const title = FormattingUtils.getTitleIfValueIsTrimmed(params.value, STATUS_MESSAGE_CHAR_LENGTH_TO_SHOW);
    return <div title={title}>{FormattingUtils.getValueTrimmed(params.value, STATUS_MESSAGE_CHAR_LENGTH_TO_SHOW)}</div>;
  },
};

export const priceCurrencyDynamicColumns = [{
  headerName: 'Contract Price',
  field: 'contractLinePrice',
  colId: 'contractLinePrice',
  valueFormatter: ({ data }: ValueFormatterParams<VarBillingUsageDetailOutput>) => {
    return data?.contractLinePrice ? NumberUtils.prettifyWithRound(data.contractLinePrice) : null;
  },
  initialWidth: 180,
  minWidth: 170,
  maxWidth: 200,
  cellStyle: () => ({ textAlign: 'right' }),
}, {
  headerName: 'Currency',
  field: 'currency',
  colId: 'currency',
  initialWidth: 160,
  minWidth: 150,
  maxWidth: 170,
}];


